import type {
  OrderListFilter,
  OrderListSearch,
  OrderListType,
} from '../model/list-params';
import {
  buildCancelledOrdersListFilter,
  buildOrdersListFilter,
  type OrderClientRef,
} from './build-list-filter';

/** Adapters from validated URL search to the snake_case API filters. */

function toValues(search: OrderListSearch) {
  return {
    search: search.search,
    limit: search.pageSize,
    offset: (search.page - 1) * search.pageSize,
    start_date: search.startDate,
    end_date: search.endDate,
  };
}

function toClient(search: OrderListSearch): OrderClientRef | null {
  return search.vendorId != null
    ? { id: search.vendorId, type: search.clientType }
    : null;
}

function toCourier(search: OrderListSearch) {
  return search.courierId != null ? { id: search.courierId } : null;
}

function toToggleFilter(search: OrderListSearch): Record<string, unknown> {
  return {
    is_market: search.isMarket,
    is_integration: search.isIntegration,
    is_business: search.isBusiness,
  };
}

/** List filter for `/orders` (and the market list via `type: 'market'`). */
export function searchToFilter(
  search: OrderListSearch,
  opts: {
    type?: OrderListType;
    isStatusPriorityCustomized?: boolean;
    statusPriorityOrder?: number[];
  } = {},
): OrderListFilter {
  const {
    type = 'orders',
    isStatusPriorityCustomized = false,
    statusPriorityOrder = [],
  } = opts;

  return buildOrdersListFilter({
    type,
    values: toValues(search),
    status: search.tab,
    stageTab: search.stageTab,
    equipmentTab: search.equipmentTab,
    cancelledByRoleTab: search.cancelledRole,
    client: toClient(search),
    courier: toCourier(search),
    idSort: search.idSort,
    filter: toToggleFilter(search),
    isStatusPriorityCustomized,
    statusPriorityOrder,
  });
}

/** List filter for the standalone `/orders/cancelled` page. */
export function searchToCancelledFilter(search: OrderListSearch): OrderListFilter {
  return buildCancelledOrdersListFilter({
    values: toValues(search),
    client: toClient(search),
    courier: toCourier(search),
    idSort: search.idSort,
    cancelledByRoleTab: search.cancelledRole,
    filter: toToggleFilter(search),
  });
}
